// ✅ TrainingScreen.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "../components/PageTitleCompoent";
import TrainingCard from "../components/TrainingCard";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";

import api from "../api";
import "../css/TrainingScreen.css";

export default function TrainingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const addedRef = useRef(false);

  const [packages, setPackages] = useState([]);
  const [editingPackage, setEditingPackage] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Fetch packages from API
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get("/training-packages/");
        const formatted = res.data.map((pkg) => ({
          id: pkg.id,
          title: pkg.package_name,
          subscribedPrice: pkg.subscribed_user_offer_amount,
          unsubscribedPrice: pkg.package_amount,
          points: pkg.services ? pkg.services.split(",") : [],
          tag: pkg.tags,
        }));
        setPackages(formatted);
      } catch (err) {
        console.error(err);
        setError("Failed to load packages.");
        toast.error(" Failed to load packages.", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  // ✅ Handle newly added package from Add Page
  useEffect(() => {
    if (location.state?.newPackage && !addedRef.current) {
      setPackages((prev) => [...prev, location.state.newPackage]);
      toast.success(" Package added successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      navigate(location.pathname, { replace: true, state: {} });
      addedRef.current = true;
    }
  }, [location.state, navigate, location.pathname]);

  // ✅ Edit package
  const handleEditClick = (pkg) => {
    setEditingPackage({ ...pkg, points: [...pkg.points] });
  };

  // ✅ Delete flow
  const handleDelete = (id) => {
    const pkg = packages.find((p) => p.id === id);
    setPackageToDelete(pkg);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!packageToDelete) return;
    try {
      await api.delete(`/training-packages/${packageToDelete.id}/`);
      setPackages((prev) => prev.filter((pkg) => pkg.id !== packageToDelete.id));
      toast.success("🗑️ Package deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error(err);
      toast.error(" Failed to delete package.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsDeleteModalOpen(false);
      setPackageToDelete(null);
    }
  };

  // ✅ Service Handlers
  const handleServiceDelete = (index) => {
    setEditingPackage((prev) => ({
      ...prev,
      points: prev.points.filter((_, i) => i !== index),
    }));
  };

  const handleServiceAdd = (newPoint) => {
    if (newPoint.trim()) {
      setEditingPackage((prev) => ({
        ...prev,
        points: [...prev.points, newPoint.trim()],
      }));
    }
  };

  // ✅ Save edits
  const handleSave = async (services, subscribedPrice, unsubscribedPrice) => {
    if (!editingPackage) return;
    try {
      setLoading(true);
      const body = {
        services: services.join(","),
        subscribed_user_offer_amount: subscribedPrice.toString(),
        package_amount: unsubscribedPrice.toString(),
      };
      await api.patch(`/training-packages/${editingPackage.id}/`, body);

      setPackages((prev) =>
        prev.map((pkg) =>
          pkg.id === editingPackage.id
            ? { ...pkg, points: services, subscribedPrice, unsubscribedPrice }
            : pkg
        )
      );
      toast.success(" Package updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      setEditingPackage(null);
    } catch (err) {
      console.error(err);
      toast.error(" Failed to save package changes.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="training-container">
      <Sidebar />
      <div className="training-main">
        <Header />
        <div className="training-content">
          <div className="heading-sec">
            <PageTitle title="Training" onBack={() => navigate(-1)} />
            <div className="add-package-sec">
              <button
                className="add-package-btn"
                onClick={() => navigate("/add_training")}
              >
                <IoMdAdd className="add-training-icon" color="#EBA8BF" /> Add
                Package
              </button>
            </div>
          </div>

          {loading && <p>Loading packages...</p>}
          {error && <p className="error-text">{error}</p>}

          {/* ✅ Card Grid */}
          <div className="training-card-grid">
            {!loading &&
              !error &&
              packages.map((pkg) => (
                <TrainingCard
                  key={pkg.id}
                  {...pkg}
                  isEditable={false}
                  onAddService={() => handleEditClick(pkg)}
                  onDelete={() => handleDelete(pkg.id)}
                />
              ))}
          </div>

          {/* ✅ Edit Modal */}
          {editingPackage && (
            <div className="training-modal-overlay">
              <div className="training-modal-box">
                <TrainingCard
                  {...editingPackage}
                  isEditable={true}
                  onClose={() => setEditingPackage(null)}
                  onSave={handleSave}
                  onAddService={handleServiceAdd}
                  onDeleteService={handleServiceDelete}
                />
              </div>
            </div>
          )}

          {/* ✅ Delete Confirmation */}
          <DeleteWithConfirmation
            isOpen={isDeleteModalOpen}
            onCancel={() => setIsDeleteModalOpen(false)}
            onConfirm={confirmDelete}
            title={`Delete ${packageToDelete?.title || "Package"}?`}
            message="Are you sure you want to delete this package?"
          />

          {/* ✅ Toast Notifications */}
       <ToastContainer
           position="top-right"
           autoClose={3000}
           hideProgressBar={false}
           newestOnTop
           closeOnClick
           pauseOnFocusLoss
           draggable
           pauseOnHover
         />
        </div>
      </div>
      
    </div>
  );
}
